import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import { trpc } from "@utils/api";

// ─────────────────────────────────────────────────────────────────────────────
// Panel data types (mirror skedyul-ui panel shapes; kept serializable for Redux)
// ─────────────────────────────────────────────────────────────────────────────

export type PanelLoadStatus = "idle" | "loading" | "succeeded" | "failed";

export type AsyncPanel<T> = {
  data: T | null;
  status: PanelLoadStatus;
  error: string | null;
  installId: string | null;
};

export type AppCrmMapOverviewData = {
  entities: Array<{
    entityHandle: string;
    status: "not_configured" | "incomplete" | "configured" | "invalid";
    targetModelId: string | null;
    targetModelName: string | null;
    targetModelHandle: string | null;
    matchFieldId: string | null;
    matchFieldHandle: string | null;
    mappedFieldCount: number;
    totalFieldCount: number;
    issueCount?: number;
    aiMetaDriftCount?: number;
  }>;
};

export type EventWiringOverviewData = {
  events: Array<{
    eventType: string;
    subscription: {
      id: string;
      isEnabled: boolean;
      triggerId: string;
      workflow: { id: string; name: string; handle: string };
    } | null;
  }>;
  extraEventTypes?: Array<{
    type: string;
    label: string;
    sourceType?: string;
    description?: string;
    icon?: string;
    recommendedWorkflowHandle?: string;
  }>;
  recommendedWorkflow?: {
    id: string;
    name: string;
    handle: string;
  } | null;
  availableWorkflows?: Array<{
    id: string;
    name: string;
    handle: string;
    triggerId: string | null;
    description?: string | null;
    eventTypes?: string[];
    isEnabled?: boolean;
  }>;
};

export type InstallSetupStepsData = {
  steps: Array<{
    id: string;
    handle: string;
    kind: "APP" | "CRM" | "REALTIME" | "ENV";
    status: "PENDING" | "READY" | "SKIPPED" | "INVALIDATED" | "BLOCKED";
    sortOrder: number;
    label: string;
    description: string | null;
    requires: string[];
    config: {
      entities?: string[];
      workflowHandles?: string[];
      envKeys?: string[];
      listenToCrm?: boolean;
      listenToEnv?: boolean;
      capabilities?: string[];
    };
    needsRepair?: boolean;
  }>;
  capabilities: Record<string, boolean>;
};

export type CrmSetupStatusValue =
  | "PENDING"
  | "COMPLETED"
  | "SKIPPED"
  | "INVALIDATED";

export type BatchOperationJobData = {
  id: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";
  label: string;
  createdAt: string;
  meta: {
    type: "app_operation";
    entity: string;
    operationHandle: string;
    progress?: {
      total: number;
      processed: number;
      succeeded: number;
      failed: number;
      skipped: number;
    };
    progressByEntity?: Record<
      string,
      { succeeded: number; failed: number; skipped: number }
    >;
    sampleFailures?: Array<{
      entity: string;
      message: string;
      code?: string;
      field?: string;
      matchValue?: unknown;
      at?: string;
    }>;
    errorTallies?: Array<{ key: string; count: number }>;
    startedAt?: string;
    completedAt?: string;
    error?: string;
  } | null;
};

export type BatchJobPanelEntry = AsyncPanel<BatchOperationJobData | null> & {
  crmConfigured: boolean | null;
  entity: string | null;
  operationHandle: string | null;
};

export type InstallPanelsState = {
  crmMapOverview: AsyncPanel<AppCrmMapOverviewData>;
  eventWiringByKey: Record<string, AsyncPanel<EventWiringOverviewData>>;
  installSetup: AsyncPanel<InstallSetupStepsData>;
  crmSetupStatus: AsyncPanel<CrmSetupStatusValue>;
  batchJobsByKey: Record<string, BatchJobPanelEntry>;
};

export function emptyAsyncPanel<T>(): AsyncPanel<T> {
  return {
    data: null,
    status: "idle",
    error: null,
    installId: null,
  };
}

export const initialInstallPanelsState: InstallPanelsState = {
  crmMapOverview: emptyAsyncPanel(),
  eventWiringByKey: {},
  installSetup: emptyAsyncPanel(),
  crmSetupStatus: emptyAsyncPanel(),
  batchJobsByKey: {},
};

export function eventWiringCacheKey(params: {
  appInstallationId: string;
  eventTypes: string[];
  recommendedWorkflowHandle?: string;
}): string {
  const types = [...params.eventTypes].sort().join(",");
  return `${params.appInstallationId}|${types}|${params.recommendedWorkflowHandle ?? ""}`;
}

export function batchJobCacheKey(params: {
  appInstallationId: string;
  entity: string;
  operationHandle: string;
}): string {
  return `${params.appInstallationId}|${params.entity}|${params.operationHandle}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const fetchCrmMapOverview = createAsyncThunk<
  { installId: string; data: AppCrmMapOverviewData },
  { appInstallationId: string },
  { state: RootState }
>("appResourcePage/fetchCrmMapOverview", async ({ appInstallationId }) => {
  const data = await trpc.apps.getAppCrmMapOverview.query({
    appInstallationId,
  });
  return { installId: appInstallationId, data };
});

export const fetchEventWiringOverview = createAsyncThunk<
  {
    key: string;
    installId: string;
    data: EventWiringOverviewData;
  },
  {
    appInstallationId: string;
    eventTypes: string[];
    recommendedWorkflowHandle?: string;
  },
  { state: RootState }
>("appResourcePage/fetchEventWiringOverview", async (input) => {
  const data = await trpc.apps.getEventWiringOverview.query({
    appInstallationId: input.appInstallationId,
    eventTypes: input.eventTypes,
    recommendedWorkflowHandle: input.recommendedWorkflowHandle,
  });
  return {
    key: eventWiringCacheKey(input),
    installId: input.appInstallationId,
    data,
  };
});

export const fetchInstallSetupSteps = createAsyncThunk<
  { installId: string; data: InstallSetupStepsData },
  { appInstallationId: string },
  { state: RootState }
>("appResourcePage/fetchInstallSetupSteps", async ({ appInstallationId }) => {
  const data = await trpc.apps.getAppInstallSetup.query({
    appInstallationId,
  });
  return { installId: appInstallationId, data };
});

export const fetchCrmSetupStatus = createAsyncThunk<
  { installId: string; data: CrmSetupStatusValue },
  { appInstallationId: string },
  { state: RootState }
>("appResourcePage/fetchCrmSetupStatus", async ({ appInstallationId }) => {
  const result = await trpc.apps.getCrmInstallSetup.query({
    appInstallationId,
  });
  return {
    installId: appInstallationId,
    data: result.crmSetupStatus as CrmSetupStatusValue,
  };
});

export const fetchLatestBatchJob = createAsyncThunk<
  {
    key: string;
    installId: string;
    entity: string;
    operationHandle: string;
    job: BatchOperationJobData | null;
    crmConfigured: boolean;
  },
  {
    appInstallationId: string;
    entity: string;
    operationHandle: string;
  },
  { state: RootState }
>("appResourcePage/fetchLatestBatchJob", async (input) => {
  const [latest, crm] = await Promise.all([
    trpc.batchOperations.getLatest.query({
      appInstallationId: input.appInstallationId,
      operationHandle: input.operationHandle,
    }),
    trpc.batchOperations.checkCrmConfigured.query({
      appInstallationId: input.appInstallationId,
      entityHandle: input.entity,
    }),
  ]);

  const meta = latest?.meta as
    | {
        type?: string;
        entity?: string;
        operationHandle?: string;
        progress?: NonNullable<BatchOperationJobData["meta"]>["progress"];
        progressByEntity?: NonNullable<
          BatchOperationJobData["meta"]
        >["progressByEntity"];
        sampleFailures?: NonNullable<
          BatchOperationJobData["meta"]
        >["sampleFailures"];
        errorTallies?: NonNullable<
          BatchOperationJobData["meta"]
        >["errorTallies"];
        startedAt?: string;
        completedAt?: string;
        error?: string;
      }
    | null
    | undefined;

  const job: BatchOperationJobData | null = latest
    ? {
        id: latest.id,
        status: latest.status as BatchOperationJobData["status"],
        label: latest.label,
        createdAt:
          latest.createdAt instanceof Date
            ? latest.createdAt.toISOString()
            : String(latest.createdAt),
        meta: meta
          ? {
              type: "app_operation" as const,
              entity: meta.entity ?? input.entity,
              operationHandle: meta.operationHandle ?? input.operationHandle,
              progress: meta.progress,
              progressByEntity: meta.progressByEntity,
              sampleFailures: meta.sampleFailures,
              errorTallies: meta.errorTallies,
              startedAt: meta.startedAt,
              completedAt: meta.completedAt,
              error: meta.error,
            }
          : null,
      }
    : null;

  return {
    key: batchJobCacheKey(input),
    installId: input.appInstallationId,
    entity: input.entity,
    operationHandle: input.operationHandle,
    job,
    crmConfigured: crm.configured,
  };
});

/**
 * Re-fetch all panels that already have data for this installation.
 * Used after entity setup finishes (replaces window focus / DOM events).
 */
export const refreshInstallPanels = createAsyncThunk<
  void,
  { appInstallationId: string } | undefined,
  { state: RootState }
>("appResourcePage/refreshInstallPanels", async (input, { getState, dispatch }) => {
  const state = getState().appResourcePage;
  const installId =
    input?.appInstallationId ??
    state.panels.crmMapOverview.installId ??
    state.panels.installSetup.installId ??
    state.panels.crmSetupStatus.installId;

  const tasks: Array<Promise<unknown>> = [];

  if (
    installId &&
    (state.panels.crmMapOverview.installId === installId ||
      state.panels.crmMapOverview.status !== "idle")
  ) {
    tasks.push(
      dispatch(fetchCrmMapOverview({ appInstallationId: installId })).unwrap(),
    );
  }

  if (installId && state.panels.installSetup.installId === installId) {
    tasks.push(
      dispatch(
        fetchInstallSetupSteps({ appInstallationId: installId }),
      ).unwrap(),
    );
  }

  if (installId && state.panels.crmSetupStatus.installId === installId) {
    tasks.push(
      dispatch(fetchCrmSetupStatus({ appInstallationId: installId })).unwrap(),
    );
  }

  for (const [key, entry] of Object.entries(state.panels.eventWiringByKey)) {
    if (!installId || entry.installId !== installId) continue;
    const [, typesPart, recommended] = key.split("|");
    const eventTypes = typesPart ? typesPart.split(",").filter(Boolean) : [];
    tasks.push(
      dispatch(
        fetchEventWiringOverview({
          appInstallationId: installId,
          eventTypes,
          recommendedWorkflowHandle: recommended || undefined,
        }),
      ).unwrap(),
    );
  }

  for (const entry of Object.values(state.panels.batchJobsByKey)) {
    if (
      !installId ||
      entry.installId !== installId ||
      !entry.entity ||
      !entry.operationHandle
    ) {
      continue;
    }
    tasks.push(
      dispatch(
        fetchLatestBatchJob({
          appInstallationId: installId,
          entity: entry.entity,
          operationHandle: entry.operationHandle,
        }),
      ).unwrap(),
    );
  }

  // If nothing was loaded yet but we have an install id, at least load CRM overview
  // so setup finish still updates status cards that will mount/refetch via refresh key.
  if (tasks.length === 0 && installId) {
    tasks.push(
      dispatch(fetchCrmMapOverview({ appInstallationId: installId })).unwrap(),
      dispatch(
        fetchInstallSetupSteps({ appInstallationId: installId }),
      ).unwrap(),
      dispatch(fetchCrmSetupStatus({ appInstallationId: installId })).unwrap(),
    );
  }

  await Promise.allSettled(tasks);
});
